import { format } from 'date-fns';
import { useStore } from 'hooks/useStore';
import { useEffect, useRef, useMemo, createElement } from 'react';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from '../../utils/pollingUtility';
import {toast} from 'react-toastify';
import ReactDOM from 'react-dom/client';
import { PrintPreviewComponent } from './PrintPreviewComponent';

// Custom hook for visibility board revamp functionality
export const useVisibilityBoardRevamp = () => {
  const { VisibilityBoardStore, roomStore, shiftTimingStore } = useStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  // Polling interval in milliseconds (15 seconds)
  const POLLING_INTERVAL = getJitteredInterval();

  const {
    selectedRoom,
    selectedShift,
    setSelectedRoom,
    setSelectedShift,
    data,
    totalBph,
    averageActualBph,
    totalVariance,
    transformedCleanData,
    isLoading,
    fetchVisibilityBoardData
  } = VisibilityBoardStore;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          roomStore.fetchRooms(1, 100),
          shiftTimingStore.fetchShiftTimings(1, 100)
        ]);
        fetchVisibilityBoardData();
      } catch (error) {
        if (environment === 'development') {
          console.error('Error fetching initial data:', error);
        }
        toast.error('Error fetching initial data!');
      }
    };

    fetchInitialData();
  }, []);

  // Polling function
  const pollData = async () => {
    try {
      await VisibilityBoardStore.pollVisibilityBoardData();
    } catch (error) {
      // Don't break polling cycle on error
      if (environment === 'development') {
        console.error('Polling error:', error);
      }
      toast.error('Error during polling for Visibility Board data!');
    }
  };

  // Start polling after initial load
  useInterval(pollData, POLLING_INTERVAL);

  // Get the selected room name (long name instead of short code)
  const selectedRoomName = useMemo(() => {
    const room = roomStore.rooms.find(r => r.room_short === selectedRoom);
    return room ? room.room_long : selectedRoom;
  }, [selectedRoom, roomStore.rooms]);

  // Custom print handler using div-based component
  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=1200,height=900');
    if (!printWindow) {
      alert('Could not open print window!');
      return;
    }

    // Write the base HTML structure
    printWindow.document.write(`
      <html>
        <head>
          <title>Visibility Board Print</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 3mm;
            }
            body, html {
              width: 100% !important;
              height: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
              font-size: 14px;
            }
            #print-root {
              height: 100% !important;
              overflow: hidden !important;
            }
            @media print {
              body {
                font-size: 13px;
              }
            }
          </style>
        </head>
        <body>
          <div id="print-root"></div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for the window to be ready, then render React component
    setTimeout(() => {
      const rootElement = printWindow.document.getElementById('print-root');
      if (rootElement) {
        const root = ReactDOM.createRoot(rootElement);
        root.render(
          createElement(PrintPreviewComponent, {
            selectedRoomName,
            selectedShift,
            bphData: data.bph,
            transformedCleanData,
            totalBph,
            numberOfBottlesInAtShiftStart: data.numberOfBottlesInAtShiftStart
          })
        );

        // Wait for React to render, then print
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 200);
      }
    }, 100);
  };

  const roomOptions = roomStore.rooms.map(room => ({
    label: `${room.room_long}`,
    value: room.room_short
  }));

  const scheduleOptions = shiftTimingStore.shiftTimings.map(item => ({
    label: `${item.shift_name}`,
    value: item.shift_name
  }));

  // Format current date for printing
  const currentDate = format(new Date(), 'MMMM dd, yyyy h:mm a');

  return {
    selectedRoom,
    selectedShift,
    setSelectedRoom,
    setSelectedShift,
    data,
    handlePrint,
    contentRef,
    totalBph,
    averageActualBph,
    totalVariance,
    transformedCleanData,
    isLoading,
    roomOptions,
    scheduleOptions,
    roomOptionsLoading: roomStore.isLoading,
    scheduleOptionsLoading: shiftTimingStore.isLoading,
    currentDate,
    selectedRoomName
  };
};
